import { useEffect, useMemo, useState } from "react";
import { chaptersOf } from "../shared/find";
import { NIGHT_BAND, PAPER_BAND, type BandPalette } from "../shared/selvedge";

/** Cyanotype's band: cream and pale blue on the print; Night Edition's: newsprint ink with the paper's red. */
const PRINT_BAND: BandPalette = { base: "#f4f0e4", soft: "#9fc3e3", gold: "#f2d48a", rust: "#ee9c7d" };
const NEWS_BAND: BandPalette = { base: "#1a1915", soft: "#56524a", gold: "#9c7424", rust: "#a52c18" };
import type { Outcome } from "../shared/story";
import { api } from "./api";
import { ClothThumb, skinNow, threadColors } from "./cloth";
import { markMorningSeen } from "./morning";
import { OUTCOME_LABEL } from "./common";
import { PlateTag, Sigil } from "./plates";
import { sound } from "./sound";
import type { StoryUI } from "./useStory";

/**
 * Endings (owner-approved, 2026-10-18; prototype in docs/ideas/loom-endings-prototype/). When a chapter ends, its ink
 * dries at once (ink.ts), a band is woven across the page in the colours of the chapter's threads, and the music
 * fades into silence; how the band ends shows the outcome: tied off clean (earned), one thread cut and frayed (at a
 * price), unravelling (lost). When the story ends: the finished cloth (cast off, with a selvedge and a knotted fringe),
 * a colophon, the last plate (drawn at once), a coda, then silence, and the keepsakes in one place. Each theme in its
 * own way: Manuscript prints a fleuron and a colophon; Phosphor types its band and ends the tape.
 *
 * The ceremony plays only for an ending that happens while the story is open; one already there when it was opened is
 * shown as its last frame.
 */

/** The latest turn when each story was opened on this page: endings after it are happening now. */
const opened = new Map<string, number>();
export function noteOpened(id: string, latest: number) { opened.set(id, latest); }
const isFresh = (id: string | null | undefined, n: number) => !!id && opened.has(id) && n > opened.get(id)!;

const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// ---- when a chapter ends ---------------------------------------------------------------------------------------------

export function ChapterBand({ s, n, at, outcome }: { s: StoryUI; n: number; at: number; outcome: Outcome }) {
  const view = s.view;
  const skin = skinNow();
  const [fresh] = useState(() => isFresh(view?.id, at));
  useEffect(() => { if (fresh) sound.closeChapter(); }, [fresh]);
  // The chapter's threads, in their cloth colours.
  const colors = useMemo(() => {
    if (!view) return [];
    const ch = chaptersOf(view).find((c) => c.k === n);
    const cols = threadColors(view, skin);
    return view.threads.filter((t) => !ch || [t.turn, ...t.seen].some((x) => x >= ch.from && x <= ch.to)).map((t) => cols.get(t.id)).filter((c): c is string => !!c);
  }, [view, n, skin]);
  if (!view) return null;
  const seed = hash(`${view.id}:chapter:${n}`);
  if (skin === "phosphor") return <TapeBand seed={seed} outcome={outcome} fresh={fresh} />;
  const pal = skin === "manuscript" ? PAPER_BAND : skin === "edition" ? NEWS_BAND : skin === "cyanotype" ? PRINT_BAND : NIGHT_BAND;
  const r = rng(seed);
  const threads: { x: number; y: number; w: number; h: number; color: string; op: number }[] = [];
  for (let x = 0; x < 600;) {
    const w = [1.6, 1.6, 2.4, 3.2][Math.floor(r() * 4)]!;
    const top = r() * 2.5, bottom = 30 - r() * 2.5, pick = r();
    const color = pick < 0.16 && colors.length ? colors[Math.floor(r() * colors.length)]! : pick < 0.6 ? pal.base : pal.soft;
    threads.push({ x, y: top, w, h: bottom - top, color, op: color === pal.base || color === pal.soft ? 0.4 + 0.35 * r() : 0.9 });
    x += w + [1.4, 2.6, 4, 7][Math.floor(r() * 4 * r())]!;
  }
  const count = threads.length, endAt = outcome === "lost" ? 0.72 : 1, cut = Math.floor(count * 0.62);
  const delay = (i: number) => (fresh ? { animationDelay: `${1400 + i * 14}ms` } : undefined);
  const last = threads.at(-1)!;
  return (
    <div className={`lm-band${fresh ? " lm-band-fresh" : ""}`} aria-hidden="true">
      <svg viewBox="0 0 600 30" preserveAspectRatio="none">
        {threads.map((t, i) => {
          const k = i / count;
          if (k > endAt) {
            // Lost: the far end unravels, threads short, slipping and fading.
            const slip = (k - endAt) / (1 - endAt);
            return <rect key={i} x={t.x} y={t.y + t.h * slip * (i % 2 ? 0.8 : 0)} width={t.w} height={t.h * (1 - slip * 0.8)} fill={t.color} opacity={t.op * (1 - slip * 0.85)}
              transform={`rotate(${(slip * 18 * (i % 2 ? 1 : -1)).toFixed(1)} ${t.x} 15)`} style={delay(i)} />;
          }
          if (outcome === "turned" && i === cut) return <rect key={i} x={t.x} y={t.y} width={3.2} height={t.h * 0.5} fill={pal.rust} style={delay(i)} />;
          return <rect key={i} x={t.x} y={t.y} width={t.w} height={t.h} fill={t.color} opacity={t.op} style={delay(i)} />;
        })}
        {outcome === "turned" && (() => {
          const t = threads[cut]!, y = t.y + t.h * 0.5, cx = t.x + 1.6;
          return <path d={`M${cx},${y} l-3,6 M${cx},${y} l0,7 M${cx},${y} l3,6 M${cx},${y} l-1.5,8`} stroke={pal.rust} strokeWidth={0.9} fill="none" strokeLinecap="round" style={delay(count)} />;
        })()}
        {outcome === "earned" && (
          <g style={delay(count)}>
            <circle cx={Math.min(594, last.x + 6)} cy={15} r={3.4} fill={pal.gold} />
            <path d={`M${Math.min(594, last.x + 6) + 3},15 q5,-6 9,-2 M${Math.min(594, last.x + 6) + 3},15 q5,6 9,2`} fill="none" stroke={pal.gold} strokeWidth={1.3} strokeLinecap="round" />
          </g>
        )}
      </svg>
      {skin === "manuscript" && <p className="lm-band-fleuron">❦</p>}
    </div>
  );
}

/** Phosphor's band: block characters typed across the line, like a tape running out. */
function TapeBand({ seed, outcome, fresh }: { seed: number; outcome: Outcome; fresh: boolean }) {
  const text = useMemo(() => {
    const r = rng(seed), glyphs = "▏▎▍▌▋▌▍ ▎▏ ", cols = 44;
    let out = "";
    for (let i = 0; i < cols; i++) {
      const k = i / cols;
      let ch = glyphs[Math.floor(r() * glyphs.length)]!;
      if (outcome === "lost" && k > 0.72) ch = r() < (k - 0.72) * 3 ? " " : r() < 0.5 ? "·" : ch;
      if (outcome === "turned" && i === Math.floor(cols * 0.62)) ch = "╳";
      out += ch;
    }
    return outcome === "earned" ? `${out} ◆` : out;
  }, [seed, outcome]);
  const [shown, setShown] = useState(fresh ? 0 : text.length);
  useEffect(() => {
    if (shown >= text.length) return;
    const t = window.setTimeout(() => setShown((c) => c + 1), shown === 0 ? 1400 : 22);
    return () => window.clearTimeout(t);
  }, [shown, text]);
  return <p className="lm-band lm-band-tape" aria-hidden="true">{text.slice(0, shown)}</p>;
}

// ---- when the story ends ---------------------------------------------------------------------------------------------

/** The story's own code for its colophon (fixed by the story): four and four hex digits, like a plate's. */
export const storyCode = (id: string) => { const h = hash(`${id}:colophon`).toString(16).toUpperCase().padStart(8, "0"); return `${h.slice(0, 4)}-${h.slice(4, 8)}`; };
const dateOf = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "");

export function StoryEnd({ s, t }: { s: StoryUI; t: { n: number; storyEnd?: { question: string; outcome: Outcome } } }) {
  const view = s.view;
  const skin = skinNow();
  const [fresh] = useState(() => isFresh(view?.id, t.n));
  useEffect(() => { if (fresh) sound.endStory(); }, [fresh]);
  // The last plate: asked for once, the first time the end card is seen.
  const last = view?.images.find((i) => i.last);
  const [drawing, setDrawing] = useState(false);
  useEffect(() => {
    if (!view || view.lastPlate || last || !view.ended) return;
    setDrawing(true);
    // The last plate's dream is shown here, on the end card: it never comes back as a morning.
    api.lastPlate(view.id).then((r) => { markMorningSeen(r.story); s.reload(); }, () => {}).finally(() => setDrawing(false));
  }, [view?.id]);
  if (!view || !t.storyEnd) return null;
  const turns = view.turns.filter((x) => x.n <= t.n).length;
  const chapters = view.turns.filter((x) => x.chapterEnd && x.n <= t.n).length;
  const places = view.been.length;
  const plates = view.images.length;
  const code = storyCode(view.id);
  const counts = `${turns} turns · ${chapters} chapter${chapters === 1 ? "" : "s"}${places ? ` · ${places} place${places === 1 ? "" : "s"}` : ""}${plates ? ` · ${plates} plate${plates === 1 ? "" : "s"}` : ""}`;
  const step = (i: number) => (fresh ? ({ ["--i" as string]: i } as React.CSSProperties) : undefined);
  return (
    <aside className={`lm-story-end${fresh ? " lm-end-fresh" : ""}`} aria-label="The end">
      <p className="lm-story-end-h" style={step(0)}>The end</p>
      <p className="lm-chapter-q" style={step(0)}>{t.storyEnd.question}</p>
      <p className="lm-chapter-outcome" style={step(0)}>{OUTCOME_LABEL[t.storyEnd.outcome]}</p>
      <div className="lm-end-cloth" style={step(1)}><ClothThumb view={view} /></div>
      <div className="lm-colophon" style={step(2)}>
        {skin === "manuscript" ? (
          <>
            <p className="lm-colophon-fleuron" aria-hidden="true">❦</p>
            <p>Here ends <em>{view.title}</em>, written by you and set by the loom in {turns} turns and {chapters} chapter{chapters === 1 ? "" : "s"}{view.begun ? `, begun ${dateOf(view.begun)}` : ""}{view.endedAt ? ` and finished ${dateOf(view.endedAt)}` : ""}.</p>
          </>
        ) : skin === "nitrate" ? (
          <>
            <p className="lm-colophon-title">Fin</p>
            <p><em>{view.title}</em><br />a picture by you and the loom, in {turns} turns and {chapters} reel{chapters === 1 ? "" : "s"}</p>
            <p>{view.begun ? `begun ${dateOf(view.begun)}` : ""}{view.endedAt ? ` · ended ${dateOf(view.endedAt)}` : ""}</p>
          </>
        ) : skin === "cyanotype" ? (
          <>
            <p>Printed by light</p>
            <p><em>{view.title}</em></p>
            <p>{counts}{view.begun ? <><br />first exposed {dateOf(view.begun)}{view.endedAt ? ` · fixed ${dateOf(view.endedAt)}` : ""}</> : null}</p>
          </>
        ) : skin === "edition" ? (
          <>
            <p className="lm-colophon-title">Final edition</p>
            <p><em>{view.title}</em>, written by you and filed by the loom: {counts}.</p>
            <p>{view.begun ? `First edition ${dateOf(view.begun)}` : ""}{view.endedAt ? ` · final ${dateOf(view.endedAt)}` : ""}</p>
          </>
        ) : skin === "phosphor" ? (
          <>
            <p>-- end of tape --</p>
            <p>{view.title.toLowerCase()}<br />{counts}<br />{view.begun ? dateOf(view.begun) : ""}{view.endedAt ? ` → ${dateOf(view.endedAt)}` : ""} · exit 0</p>
          </>
        ) : (
          <>
            <p>Woven by you and the loom</p>
            <p><em>{view.title}</em></p>
            <p>{counts}{view.begun ? <><br />begun {dateOf(view.begun)}{view.endedAt ? ` · ended ${dateOf(view.endedAt)}` : ""}</> : null}</p>
          </>
        )}
        <Sigil code={code} size={44} />
        <p className="lm-colophon-code">{code}{skin === "phosphor" && <span className={`lm-tape-cursor${fresh ? " lm-tape-cursor-fresh" : ""}`} aria-hidden="true" />}</p>
      </div>
      {last ? (
        <figure className="lm-plate lm-last-plate" style={step(3)}>
          <img className="lm-develop" src={api.imageUrl(view.id, last.i)} alt={last.caption} />
          <figcaption>{last.caption}</figcaption>
          {last.code && <PlateTag src={api.imageUrl(view.id, last.i)} plate={{ i: last.i, code: last.code, caption: last.caption, ...(last.drawnAt ? { drawnAt: last.drawnAt } : {}) }} />}
        </figure>
      ) : drawing ? <p className="lm-gate-small lm-last-plate-wait" style={step(3)}>The loom is drawing the last plate…</p> : null}
      <div className="lm-chapter-offer lm-keepsakes" style={step(4)}>
        <button type="button" onClick={() => window.dispatchEvent(new Event("weftwake:cloth"))}>Save the cloth</button>
        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("weftwake:map", { detail: "places" }))}>See the map</button>
        <button type="button" onClick={() => window.dispatchEvent(new Event("weftwake:passon"))}>Pass the opening on</button>
        <a href="/plates">Your plates</a>
      </div>
      <p className="lm-gate-small" style={step(4)}>You can stay a while in this world, or begin another story.</p>
      <div className="lm-chapter-offer" style={step(4)}><a href="/">Begin another story</a></div>
    </aside>
  );
}
